import axios from 'axios';
import { logger } from '../../../utils/logger';

const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;

export interface CheckOAuthResponse {
  exists: boolean;
  isOAuth: boolean;
  userStatus: string;
  emailVerified: boolean;
  hasClientId: boolean;
  clientId: string | null;
  role: string | null;
  username: string;
}

export interface UpdateAttributesResponse {
  message: string;
  username: string;
  emailVerified?: boolean;
}

export interface UserDetailsResponse {
  username: string;
  userStatus: string;
  enabled: boolean;
  userCreateDate: string;
  userLastModifiedDate: string;
  attributes: Record<string, string>;
  isOAuth: boolean;
}

class CognitoOAuthEndpoint {
  private static instance: CognitoOAuthEndpoint;
  private baseUrl: string;

  private constructor() {
    this.baseUrl = `${PUBLIC_API_URL}/cognito/oauth`;
  }

  public static getInstance(): CognitoOAuthEndpoint {
    if (!CognitoOAuthEndpoint.instance) {
      CognitoOAuthEndpoint.instance = new CognitoOAuthEndpoint();
    }
    return CognitoOAuthEndpoint.instance;
  }

  /**
   * Check if a user exists and their OAuth status
   */
  async checkOAuthUser(email: string): Promise<CheckOAuthResponse> {
    try {
      const response = await axios.post<CheckOAuthResponse>(`${this.baseUrl}/check-oauth`, {
        email,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to check OAuth user:', error);
      throw error;
    }
  }

  /**
   * Update Cognito user custom attributes (clientId, role, and optionally emailVerified)
   */
  async updateUserAttributes(
    email: string,
    clientId: string,
    role: string,
    emailVerified?: boolean
  ): Promise<UpdateAttributesResponse> {
    try {
      const payload: any = {
        email,
        clientId,
        role,
      };

      // Only include emailVerified if explicitly set
      if (emailVerified !== undefined) {
        payload.emailVerified = emailVerified;
      }

      const response = await axios.post<UpdateAttributesResponse>(
        `${this.baseUrl}/update-attributes`,
        payload
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to update user attributes:', error);
      throw error;
    }
  }

  /**
   * Get detailed user information
   */
  async getUserDetails(email: string): Promise<UserDetailsResponse> {
    try {
      const response = await axios.post<UserDetailsResponse>(`${this.baseUrl}/get-user-details`, {
        email,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to get user details:', error);
      throw error;
    }
  }

  /**
   * Link an OAuth provider to an existing user
   */
  async linkProvider(
    destinationEmail: string,
    providerName: string,
    providerUserId: string
  ): Promise<{ message: string }> {
    try {
      const response = await axios.post<{ message: string }>(`${this.baseUrl}/link-provider`, {
        destinationEmail,
        providerName,
        providerUserId,
      });
      return response.data;
    } catch (error) {
      logger.error('Failed to link provider:', error);
      throw error;
    }
  }
}

export const cognitoOAuthEndpoint = CognitoOAuthEndpoint.getInstance();
